import { DateTime } from 'luxon';
import mixpanel from 'mixpanel-browser';

import { featureFlagsSelectors, featureFlagsSimplifi } from 'app-components/flux/feature-flags';
import { transactionsSelectors } from 'app-components/flux/transactions';
import { getCommonConfig } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';

import { getTTI } from 'utils/tti';
import { BUILD_TYPE, Config } from './config';

import platform from './platform';

const clientType = 'Simplifi Web App';
const mixpanelToken = Config?.builds?.[BUILD_TYPE]?.mixpanel_token || 'f02e19059c63af74c567d59435e16a1d'; // test environment by default
mixpanel.init(mixpanelToken, {
  cookie_name: 'web_app',
  cross_subdomain_cookie: false,
  persistence: 'localStorage',
  persistence_name: 'web_app',
  ignore_dnt: true,
});
export const initialApiHost = mixpanel.get_config('api_host');
const isProxyEnabled = () => {
  const mixpanelProxy = store
    ? featureFlagsSelectors.getFeatureFlagEnsured(store.getState(), 'mixpanelProxy')
    : undefined;
  return typeof mixpanelProxy === 'boolean'
    ? mixpanelProxy
    : featureFlagsSimplifi.featureFlagsSimplifiDefaults.mixpanelProxy;
};
const proxyApiHost = getCommonConfig()?.mixpanelProxy;
assert(proxyApiHost, 'mixpanelProxy configuration not found');
if (isProxyEnabled() && proxyApiHost) {
  mixpanel.set_config({ api_host: proxyApiHost });
}

const deviceType = platform.device?.type || (platform.device.type === 'tablet' && 'iPad') || 'desktop';

// Initialize ischomebook TWA prop.
let isChromebookTWA = false;
const isChromebook = async () => {
  try {
    isChromebookTWA = await platform?.isChromebook?.();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Failed to check isChromebook', err);
  }
};

isChromebook();

// Mixpanel handles $device and $model automatically
mixpanel.register({
  client_type: clientType,
  quicken_year: DateTime.local().year,
  $app_version_string: process.env.APP_VERSION,
  $app_build_number: process.env.BUILD_NUMBER,
  pwa: window?.matchMedia?.('(display-mode: standalone)').matches,
  ...(deviceType ? { $device: deviceType } : undefined), // mixpanel has issues with $device detection for desktop and iPad - overriding it
});

export const dynamicDefaultPropertiesCreator = () => {
  const infoProps = mixpanel._?.info?.properties?.();
  const persistedProps = mixpanel.persistence?.props;
  const unpersistedSuperProps = mixpanel.unpersisted_superprops;
  const dynamicDefaultProps = {
    ...infoProps,
    ...persistedProps,
    ...unpersistedSuperProps,
  };
  const transactions = store ? transactionsSelectors.getResourcesById(store.getState()) : null;
  dynamicDefaultProps.sec_gpc = navigator?.globalPrivacyControl;
  dynamicDefaultProps.dnt = navigator?.doNotTrack;
  dynamicDefaultProps.tti = getTTI();
  dynamicDefaultProps.transactions = transactions?.size;
  dynamicDefaultProps.proxy = isProxyEnabled();
  dynamicDefaultProps.pathname = window?.location.pathname;
  dynamicDefaultProps.pageLocation = document.title.replace('Simplifi - ', '');
  dynamicDefaultProps.memory_device =
    typeof navigator.deviceMemory === 'number' ? navigator.deviceMemory * 1024 ** 3 : undefined;
  dynamicDefaultProps.memory_limit = window?.performance?.memory?.jsHeapSizeLimit || undefined;
  dynamicDefaultProps.memory_used = window?.performance?.memory?.usedJSHeapSize;
  if (
    typeof window?.performance?.memory?.jsHeapSizeLimit === 'number' &&
    typeof window?.performance?.memory?.usedJSHeapSize === 'number'
  ) {
    // eslint-disable-next-line no-unsafe-optional-chaining
    dynamicDefaultProps.memory_available =
      window?.performance?.memory?.jsHeapSizeLimit - window?.performance?.memory?.usedJSHeapSize;
  } else {
    dynamicDefaultProps.memory_available = undefined;
  }
  dynamicDefaultProps.is_chromebook_twa = isChromebookTWA;

  // A/B tests

  return dynamicDefaultProps;
};

export default mixpanel;
